/**
 * @file ExtractedInfoPage.tsx
 *
 * @description A React component that displays extracted information from incident reports.
 * This page allows police officers to review, edit, and submit incident information to RMS.
 *
 * @module Views
 * @requires React
 * @requires Material-UI
 */
import {
  Box,
  Button,
  Skeleton,
  alpha,
  useTheme,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PreviewIcon from "@mui/icons-material/Preview";
import { usePageHeader } from "../../context/LayoutContext";
import { ExtractedInformationV2 } from "./ExtractedInformationV2";
import * as JsonRefs from "json-refs";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";
import { createRef, useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { VideoData } from "../../shared/interfaces/VideoData";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import { Download } from "@mui/icons-material";
import { saveAs } from "file-saver";
import { LoadingButton } from "@mui/lab";
import { ReportSubmitModal } from "../../modals/ReportSubmitModal";
import {
  VideoPreviewV2,
  type VideoPreviewHandle,
} from "../../components/video/VideoPreviewV2";
import { resolveCitationTarget } from "../../features/narrative-citations/resolveCitationTarget";
import type { CitationGroup } from "../../features/narrative-citations/types";
import { DownloadReportModal } from "../../modals/DownloadReportModal";
import { ExtractedInformationSkeleton } from "../../components/skeletons/ExtractedInformationSkeleton";
import type { JSONSchema7 } from "json-schema";
import { FieldMapping } from "../../config/fieldMapping";
import { eforceSchemaMapping } from "../../config/eforceSchemaMapping";
import { genericSchemaMapping } from "../../config/genericSchemaMapping";
import {
  getEvidenceData,
  getIncidentData,
  getReportMetadata,
} from "../../api/reportApi";
import { rmsClient } from "../../api/clients";
import { filterSchemaByData } from "../../utils/filterSchemaByData";

/**
 * Props for the ExtractedInfoPage component
 * @interface ExtractedInfoPageProps
 * @property {boolean} [isFinalReview] - Indicates if this is the final review stage
 * @property {React.MutableRefObject<EforceIncidentModel | null> | undefined} [incidentModelRef] - Reference to the incident model
 * @property {React.MutableRefObject<{[narrativeID: string]: React.MutableRefObject<boolean>}>} [narrativeEasterEggRefs] - References for tracking easter eggs
 * @property {function} [onEasterEggFound] - Callback when an easter egg is found
 */
interface ExtractedInfoPageProps {
  isFinalReview?: boolean;
  incidentModelRef?:
    | React.MutableRefObject<EforceIncidentModel | null>
    | undefined;
  narrativeEasterEggRefs?: React.MutableRefObject<{
    [narrativeID: string]: React.MutableRefObject<boolean>;
  }>;
  jsonSchema?: JSONSchema7;
  schemaMapping?: Record<string, FieldMapping>;
  onEasterEggFound?: () => void;
  stationId?: string;
}

/**
 * @name ExtractedInfoPage
 *
 * @description Component that displays extracted information from an incident report
 *
 * This component handles:
 * - Fetching and displaying extracted incident information
 * - Editing incident information
 * - Committing reports to RMS
 * - Downloading incident reports
 * - Displaying video previews
 *
 * @param {ExtractedInfoPageProps} props - Component props
 *
 * @returns {JSX.Element} The rendered component
 */
export const ExtractedInfoPage: React.FC<ExtractedInfoPageProps> = ({
  isFinalReview,
  incidentModelRef,
  narrativeEasterEggRefs,
  onEasterEggFound,
  jsonSchema: providedJsonSchema,
  schemaMapping: providedSchemaMapping,
  stationId,
}) => {
  const theme = useTheme();

  const [incident, setIncident] = useState<EforceIncidentModel | null>(null);

  // Report status states
  const [isReportSigned, setIsReportSigned] = useState<boolean>(false);
  const [isReportCommitted, setIsReportCommitted] = useState<boolean>(false);
  const [editable, setEditable] = useState<boolean>(false);
  const [videoData, setVideoData] = useState<VideoData | null | VideoData[]>(
    null,
  );
  const [jsonSchema, setJsonSchema] = useState<JSONSchema7 | null>(
    providedJsonSchema || null,
  );
  const [schemaMapping, setSchemaMapping] = useState<Record<
    string,
    FieldMapping
  > | null>(providedSchemaMapping || null);

  // UI control states
  const [isCommitting, setIsCommitting] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [showRMSCommit, setShowRMSCommit] = useState<boolean>(false);
  const [showDownloadLanguageModal, setShowDownloadLanguageModal] =
    useState(false);
  const [refsObject, setRefsObject] = useState<
    Record<string, React.RefObject<HTMLDivElement>>
  >({});
  const [rms, setRMS] = useState<string | null>(null);

  // Hooks for navigation, error handling, etc.
  const { showError, snackBarMessage, snackBarMode, handleCloseSnackbar } =
    useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();
  const { reportID } = useParams();
  const navigate = useNavigate();

  // Show video preview if report is signed and video data exists
  const showVideoPreview = videoData && isReportSigned;

  // Imperative handle to the player so citation anchors can seek it.
  const videoPreviewRef = useRef<VideoPreviewHandle>(null);
  // The player stays mounted; in final review it starts minimized (a slim timed
  // scrubber) and expands when a citation is clicked.
  const [videoMinimized, setVideoMinimized] = useState<boolean>(
    Boolean(isFinalReview),
  );

  // A citation anchor was clicked: reveal + seek the matching source video to
  // the cited moment (snapping to the containing transcript paragraph if known).
  const handleCite = useCallback(
    (group: CitationGroup) => {
      const citation = group.citations[0];
      if (!citation) return;
      setVideoMinimized(false);
      const videos = Array.isArray(videoData)
        ? videoData
        : videoData
          ? [videoData]
          : [];
      const video = videos.find((v) => v.id === citation.evidenceId);
      const paragraphs = (video?.transcript as { paragraphs?: unknown[] } | undefined)
        ?.paragraphs as Parameters<typeof resolveCitationTarget>[1];
      const target = resolveCitationTarget(citation, paragraphs ?? null);
      videoPreviewRef.current?.seekToEvidence(
        citation.evidenceId,
        target.seconds,
      );
    },
    [videoData],
  );

  /**
   * @name createSectionRefs
   *
   * @description Creates references for each section in the incident model
   *
   * @returns {Record<string, React.RefObject<HTMLDivElement>>} Object with section names as keys and refs as values
   */
  const createSectionRefs = useCallback(() => {
    const refs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
    if (incident) {
      Object.entries(incident).forEach(([section, value]) => {
        if (Array.isArray(value)) {
          refs[section] = createRef<HTMLDivElement>();
        }
      });
    }
    return refs;
  }, [incident]);

  /**
   * Creates refs for each section of the incident for scrolling functionality
   *
   * @returns {Record<string, React.RefObject<HTMLDivElement>>} Object containing refs for each section
   */
  useEffect(() => {
    // Create refs for each section of the incident
    if (refsObject && Object.keys(refsObject).length === 0) {
      const refs = createSectionRefs();
      // Only set refsObject if refs contains keys; avoid setting empty objects which would
      // trigger re-renders and cause a maximum update depth exceeded error.
      if (refs && Object.keys(refs).length > 0) {
        setRefsObject(refs);
      }
    }
  }, [incident, createSectionRefs, refsObject]);

  /**
   * Fetches the extracted incident data from the API
   */
  const fetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;

    try {
      const [metadataResult, incidentResult] = await Promise.allSettled([
        getReportMetadata(reportID),
        getIncidentData(reportID),
      ]);

      if (metadataResult.status === "fulfilled") {
        const metadata = metadataResult.value;
        setEditable(metadata.canEdit);
        setIsReportSigned(
          ["SIGNED", "RMS COMMITTED"].includes(metadata.reportStatus),
        );
        setIsReportCommitted(metadata.reportStatus === "RMS COMMITTED");
        setShowRMSCommit(![null, "", "KAR"].includes(metadata.rms));
        setRMS(metadata.rms);

        // Fetch evidence data using metadata evidenceIds
        Promise.all(metadata.evidenceIds.map((id) => getEvidenceData(id)))
          .then((evidenceResults) => {
            setVideoData(evidenceResults);
          })
          .catch((err) => {
            console.error("Error fetching evidence data:", err);
            showError(
              "An error occurred while fetching evidence data. Please try again later.",
            );
          });
      } else {
        throw metadataResult.reason;
      }

      if (incidentResult.status === "fulfilled") {
        setIncident(incidentResult.value.reportContent);
      } else {
        throw incidentResult.reason;
      }
    } catch (error: any) {
      showError(
        error?.response?.data?.error ||
          "An error occurred while fetching report information. Please try again later.",
      );
    }
  }, [reportID, showError]);

  // Set page titles based on current view mode
  const pageTitle = isFinalReview ? "Final Review" : "Generated Report Review";

  const pageSubTitle = isFinalReview
    ? "Review the information once more before submitting"
    : "Review the extracted information from your video evidence";

  // Set page header in app bar (only when not used as child component)
  usePageHeader({
    title: pageTitle,
    subtitle: pageSubTitle,
    icon: <PreviewIcon />,
  });

  // Opens the download language selection modal
  const showDownloadModal = () => {
    setShowDownloadLanguageModal(true);
  };

  // Closes the download language selection modal
  const handleCloseDownloadModal = () => {
    setShowDownloadLanguageModal(false);
  };

  /**
   * @name handleDownloadIncident
   *
   * @description Downloads the incident report in the selected language
   *
   * @param {string} selectedLanguageCode - The language code for the report
   *
   * @returns {Promise<void>} A promise that resolves when the download is complete
   */
  const handleDownloadIncident = (selectedLanguageCode: string) => {
    return rmsClient
      .get("/report/download", {
        params: { reportId: reportID, languageCode: selectedLanguageCode },
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
        },
      })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/pdf",
        });
        saveAs(blob, `incident-information-${reportID}.pdf`);
        setShowDownloadLanguageModal(false);
      })
      .catch((error) => {
        showError(
          error.response?.data?.error ||
            "An error occurred while downloading the document. Please try again later.",
        );
      });
  };

  // Fetch incident data or use provided reference
  useEffect(() => {
    if (!isFinalReview) {
      fetchExtractedIncident();
    } else if (incidentModelRef) {
      // set current incident and sparse info to edited versions
      setIncident(incidentModelRef.current);
    }
  }, [isFinalReview, fetchExtractedIncident, incidentModelRef]);

  // Navigate to edit section if conditions are met
  useEffect(() => {
    if (editable && !isReportSigned && !isReportCommitted && incident) {
      navigate(`/edit-section/${reportID}`);
    }
  }, [
    editable,
    isReportSigned,
    isReportCommitted,
    incident,
    reportID,
    navigate,
  ]);

  useEffect(() => {
    // fetch json schema if not provided
    if (!jsonSchema && !schemaMapping) {
      rmsClient
        .get("/schema", {})
        .then((response) =>
          JsonRefs.resolveRefs(response.data.schema as JSONSchema7, {
            filter: "local",
          }),
        )
        .then((result) => {
          let resolvedSchema = result.resolved as JSONSchema7;
          if (incident && jsonSchema && rms === "KAR") {
            // slice schema if KAR to only include sections present in incident data
            const filteredSchema = filterSchemaByData(incident, jsonSchema);
            resolvedSchema = filteredSchema;
          } 
          setJsonSchema(resolvedSchema);
          switch (rms) {
            case "EF":
              setSchemaMapping(eforceSchemaMapping);
              break;
            default:
              setSchemaMapping(genericSchemaMapping);
              break;
          }
        });
    }
  }, [rms, jsonSchema, schemaMapping]);

  // Submits the incident report to the RMS system
  const handleRMSCommit = async () => {
    if (isCommitting) return;
    setOpenConfirmation(false);
    if (!incident) {
      showError("No incident data available.");
      return;
    }
    setIsCommitting(true);
    rmsClient
      .post("/report/rms-commit", { reportId: reportID! })
      .then((res) => {
        const eventNumber = res.data.eventNumber;
        navigate("/confirmation/" + eventNumber, {
          state: { reportID },
        });
      })
      .catch((err) => {
        console.error(err);
        showError(
          err.response.data.error ||
            "An error occurred while submitting the report to the RMS servers. Please try again later.",
        );
      })
      .finally(() => {
        setIsCommitting(false);
      });
  };

  // Renders a loading skeleton when incident data is not yet available
  if (!incident || !jsonSchema || !schemaMapping) {
    return (
      <>
        <SnackBarMessage
          handleClose={handleCloseSnackbar}
          message={snackBarMessage}
          mode={snackBarMode}
          horizontalAnchor="center"
          verticalAnchor="top"
          sxOverrides={snackbarPosition}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: { xs: 1, sm: 2, md: 3 },
            width: "75vw",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              {/* Button */}
              <Box
                display="flex"
                justifyContent="center"
                width="100%"
                sx={{ my: 2 }}
              >
                <Skeleton width="20%" height={100} />
              </Box>
            </Grid>
          </Grid>
          <ExtractedInformationSkeleton />
        </Box>
      </>
    );
  }

  return (
    <>
      <SnackBarMessage
        handleClose={handleCloseSnackbar}
        message={snackBarMessage}
        mode={snackBarMode}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />
      <ReportSubmitModal
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleSubmitReport={handleRMSCommit}
        isRMSCommit
      />
      <DownloadReportModal
        showDownloadLanguageModal={showDownloadLanguageModal}
        handleCloseDownloadModal={handleCloseDownloadModal}
        handleDownloadIncident={handleDownloadIncident}
      />
      {/* Action Buttons and Video Preview - only show when not used as child component */}
      {!isFinalReview && (
        <Container maxWidth="xl" sx={{ pt: 2, pb: 1 }}>
          {/* Action Buttons */}
          {!editable && isReportSigned && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={showDownloadModal}
                startIcon={<Download />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  boxShadow: `0 4px 14px ${alpha(
                    theme.palette.primary.main,
                    0.3,
                  )}`,
                  "&:hover": {
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.primary.main,
                      0.4,
                    )}`,
                  },
                }}
              >
                Download Document
              </Button>
              {!isReportCommitted && showRMSCommit && (
                <LoadingButton
                  onClick={() => setOpenConfirmation(true)}
                  variant="contained"
                  disabled={isCommitting || !incident}
                  loading={isCommitting}
                  sx={{
                    ml: 2,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    py: 1.2,
                    boxShadow: `0 4px 14px ${alpha(
                      theme.palette.primary.main,
                      0.3,
                    )}`,
                    "&:hover": {
                      boxShadow: `0 6px 20px ${alpha(
                        theme.palette.primary.main,
                        0.4,
                      )}`,
                    },
                  }}
                >
                  COMMIT DATA TO RMS
                </LoadingButton>
              )}
            </Box>
          )}

        </Container>
      )}

      {/* Player is mounted wherever citations can appear (signed or final review)
          so anchors can always drive it — never unmounted. In final review it
          starts as a slim timed scrubber and expands when a citation is clicked. */}
      {videoData && (showVideoPreview || isFinalReview) && (
        <Container maxWidth="xl" sx={{ pt: 2, pb: 1 }}>
          <VideoPreviewV2
            ref={videoPreviewRef}
            videoData={videoData}
            isMinimizable={true}
            isMinimized={videoMinimized}
            onToggleMinimize={() => setVideoMinimized((v) => !v)}
          />
        </Container>
      )}

      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <ExtractedInformationV2
              formData={incident}
              jsonSchema={jsonSchema}
              schemaMapping={schemaMapping}
              title="Extracted Information"
              narrativeEasterEggRefs={narrativeEasterEggRefs}
              onEasterEggFound={onEasterEggFound}
              stationId={stationId}
              onCite={handleCite}
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
